import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './LandingPage.css';

interface LandingConfig {
  landingRedirectUrl: string;
  icpNumber: string;
  policeRecordNumber: string;
}

interface TimelineEntry {
  content: string;
  date: string;
}

const defaultTimeline: TimelineEntry[] = [
  { content: '敬请期待', date: '2026.6' },
  { content: '注册域名mytomato.cn', date: '2026.5' },
  { content: '搭建第一个网站', date: '2026.01' },
];

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const configRef = useRef<LandingConfig>({ landingRedirectUrl: '/login', icpNumber: '', policeRecordNumber: '' });
  const [icpNumber, setIcpNumber] = useState('');
  const [policeRecordNumber, setPoliceRecordNumber] = useState('');
  const [configLoaded, setConfigLoaded] = useState(false);
  const [timeline, setTimeline] = useState<TimelineEntry[]>(defaultTimeline);

  const [theme, setTheme] = useState<'Light' | 'Dark'>(() => {
    const saved = localStorage.getItem('landingTheme');
    return (saved as 'Light' | 'Dark') || 'Light';
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 600);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    fetch('/api/landing-config')
      .then(res => res.json())
      .then(data => {
        if (data?.data) {
          if (data.data.landingRedirectUrl) {
            configRef.current.landingRedirectUrl = data.data.landingRedirectUrl;
          }
          if (data.data.icpNumber) {
            configRef.current.icpNumber = data.data.icpNumber;
            setIcpNumber(data.data.icpNumber);
          }
          if (data.data.policeRecordNumber) {
            configRef.current.policeRecordNumber = data.data.policeRecordNumber;
            setPoliceRecordNumber(data.data.policeRecordNumber);
          }
        }
        setConfigLoaded(true);
      })
      .catch(() => {
        setConfigLoaded(true);
      });
  }, []);

  useEffect(() => {
    fetch('/api/home-content/timeline')
      .then(res => res.json())
      .then(data => {
        if (data?.status === 200 && Array.isArray(data?.data?.timeline) && data.data.timeline.length > 0) {
          const fetched = data.data.timeline as TimelineEntry[];
          if (fetched.every(item => item && typeof item.content === 'string' && typeof item.date === 'string')) {
            setTimeline(fetched);
          }
        }
      })
      .catch(() => {
        // 使用默认时光轴
      });
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-landing-theme', theme);
    localStorage.setItem('landingTheme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'Light' ? 'Dark' : 'Light');
  };

  if (loading) {
    return (
      <div className="landing-loading">
        <div className="landing-loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="landing-page">
      <div className="landing-filter"></div>

      <div className="landing-main">
        <div className="landing-left">
          <div className="landing-logo" style={{ backgroundImage: 'url(/homepage/img/logo.png)' }}>
            <img
              style={{ position: 'absolute', top: '-15%', left: '-10%', width: '120%', aspectRatio: '1/1' }}
              src="/homepage/img/logokuang.png"
              alt=""
            />
          </div>
          <div className="landing-left-div landing-left-des">
            <div className="landing-left-des-item">
              <svg viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
                <path d="M512 249.976471c-99.388235 0-180.705882 81.317647-180.705882 180.705882s81.317647 180.705882 180.705882 180.705882 180.705882-81.317647 180.705882-180.705882-81.317647-180.705882-180.705882-180.705882z m0 301.17647c-66.258824 0-120.470588-54.211765-120.470588-120.470588s54.211765-120.470588 120.470588-120.470588 120.470588 54.211765 120.470588 120.470588-54.211765 120.470588-120.470588 120.470588z" />
                <path d="M512 39.152941c-216.847059 0-391.529412 174.682353-391.529412 391.529412 0 349.364706 391.529412 572.235294 391.529412 572.235294s391.529412-222.870588 391.529412-572.235294c0-216.847059-174.682353-391.529412-391.529412-391.529412z m0 891.482353C424.658824 873.411765 180.705882 686.682353 180.705882 430.682353c0-183.717647 147.576471-331.294118 331.294118-331.294118s331.294118 147.576471 331.294118 331.294118c0 256-243.952941 442.729412-331.294118 499.952941z" />
              </svg>
              China-Henan
            </div>
            <div className="landing-left-des-item">
              <svg viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
                <path d="M729.6 234.666667H294.4V157.866667a51.2 51.2 0 0 1 51.2-51.2h332.8a51.2 51.2 0 0 1 51.2 51.2v76.8z m179.2 51.2a51.2 51.2 0 0 1 51.2 51.2v512a51.2 51.2 0 0 1-51.2 51.2H115.2a51.2 51.2 0 0 1-51.2-51.2v-512a51.2 51.2 0 0 1 51.2-51.2h793.557333z m-768 172.032c0 16.384 13.312 29.696 29.696 29.696h683.008a29.696 29.696 0 1 0 0-59.392H170.410667a29.696 29.696 0 0 0-29.696 29.696z m252.416 118.784c0 16.384 13.312 29.696 29.696 29.696h178.176a29.696 29.696 0 1 0 0-59.392H422.912a29.738667 29.738667 0 0 0-29.696 29.696z" />
              </svg>
              Sias
            </div>
          </div>
          <div className="landing-left-div landing-left-tag">
            <div className="landing-left-tag-item">网页</div>
            <div className="landing-left-tag-item">linux</div>
            <div className="landing-left-tag-item">服务器</div>
            <div className="landing-left-tag-item">前端</div>
            <div className="landing-left-tag-item">Docker</div>
          </div>
          <div className="landing-left-div landing-left-time">
            <ul className="landing-timeline">
              {timeline.map((item, index) => (
                <li key={index}>
                  <div className="landing-focus"></div>
                  <div>{item.content}</div>
                  <div>{item.date}</div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="landing-right">
          <header className="landing-header">
            <div className="landing-index-logo" style={{ backgroundImage: 'url(/homepage/img/logo.png)' }}>
              <img
                style={{ position: 'absolute', top: '-15%', left: '-10%', width: '120%', aspectRatio: '1/1' }}
                src="/homepage/img/logokuang.png"
                alt=""
              />
            </div>
            <div className="landing-welcome">
              Hello I' m <span className="landing-gradientText">Tomato</span>
            </div>
            <div className="landing-description">
              👦 <span className="landing-purpleText">Lazy</span> Bum
            </div>
            <div className="landing-description">
              📝 The only way to do <span className="landing-purpleText landing-textBackground">great</span> is
              to <span className="landing-purpleText landing-textBackground">love</span> what you do.
            </div>

            <div className="landing-iconContainer">
              <a className="landing-iconItem" href="https://mytomato.cn" target="_blank" rel="noopener noreferrer">
                <svg viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
                  <path d="M511.6 76.3C264.3 76.2 64 276.4 64 523.5 64 718.9 189.3 885 363.8 946c23.5 5.9 19.9-10.8 19.9-22.2v-77.5c-135.7 15.9-141.2-73.9-150.3-88.9C215 726 171.5 718 184.5 703c30.9-15.9 62.4 4 98.9 57.9 26.4 39.1 77.9 32.5 104 26 5.7-23.5 17.9-44.5 34.7-60.8-140.6-25.2-199.2-111-199.2-213 0-49.5 16.3-95 48.3-131.7-20.4-60.5 1.9-112.3 4.9-120 58.1-5.2 118.5 41.6 123.2 45.3 33-8.9 70.7-13.6 112.9-13.6 42.4 0 80.2 4.9 113.5 13.9 11.3-8.6 67.3-48.8 121.3-43.9 2.9 7.7 24.7 58.3 5.5 118 32.4 36.8 48.9 82.7 48.9 132.3 0 102.2-59 188.1-200 212.9 23.5 23.2 38.1 55.4 38.1 91v112.5c0.8 9 0 17.9 15 17.9 177.1-59.7 304.6-227 304.6-424.1 0-247.2-200.4-447.3-447.5-447.3z" />
                </svg>
                <div className="landing-iconTip">Github</div>
              </a>
              <a className="landing-iconItem" href="https://mytomato.cn">
                <svg viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
                  <path d="M926.47619 355.644952V780.190476a73.142857 73.142857 0 0 1-73.142857 73.142857H170.666667a73.142857 73.142857 0 0 1-73.142857-73.142857V355.644952l304.103619 257.828572a170.666667 170.666667 0 0 0 220.745142 0L926.47619 355.644952zM853.333333 170.666667a74.044952 74.044952 0 0 1 26.087619 4.778666 72.704 72.704 0 0 1 30.622477 22.186667 73.508571 73.508571 0 0 1 10.678857 17.67619c3.169524 7.509333 5.12 15.652571 5.607619 24.210286L926.47619 243.809524v24.380952L559.469714 581.241905a73.142857 73.142857 0 0 1-91.306666 2.901333l-3.632762-2.925714L97.52381 268.190476v-24.380952a72.899048 72.899048 0 0 1 40.155428-65.292191A72.97219 72.97219 0 0 1 170.666667 170.666667h682.666666z" />
                </svg>
                <div className="landing-iconTip">Mail</div>
              </a>
              <a className="landing-switch" href="javascript:void(0)" onClick={(e) => { e.preventDefault(); toggleTheme(); }}>
                <div className="landing-onoffswitch">
                  <input
                    type="checkbox"
                    className="landing-onoffswitch-checkbox"
                    checked={theme === 'Light'}
                    onChange={toggleTheme}
                  />
                  <label className="landing-onoffswitch-label">
                    <span className="landing-onoffswitch-inner"></span>
                    <span className="landing-onoffswitch-switch"></span>
                  </label>
                </div>
              </a>
            </div>

            <div className="landing-tanChiShe">
              <img
                src={theme === 'Light' ? '/homepage/svg/snake-Light.svg' : '/homepage/svg/snake-Dark.svg'}
                alt=""
              />
            </div>
          </header>

          <div className="landing-content">
            <div className="landing-title">
              <svg viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
                <path d="M629.333333 202.666667v213.333333h277.333334v448h-512v-213.333333h-277.333334v-448h512z m213.333334 277.333333h-213.333334v170.666667h-170.666666v149.333333h384v-320z m-277.333334-213.333333h-384v320h213.333334v-170.666667h170.666666v-149.333333z m0 213.333333h-106.666666v106.666667h106.666666v-106.666667z" />
              </svg>
              site
            </div>
            <div className="landing-projectList">
              <a className="landing-projectItem landing-a" href="/blog" rel="noopener noreferrer">
                <div className="landing-projectItemLeft">
                  <h1>博客</h1>
                  <p>记录摆烂日常</p>
                </div>
                <div className="landing-projectItemRight">
                  <img src="/homepage/img/i1.png" alt="" />
                </div>
              </a>
              <a className="landing-projectItem landing-a" href="/cloud-disk" rel="noopener noreferrer">
                <div className="landing-projectItemLeft">
                  <h1>云盘</h1>
                  <p>存储收集文件</p>
                </div>
                <div className="landing-projectItemRight">
                  <img src="/homepage/img/i2.png" alt="" />
                </div>
              </a>
              <a className="landing-projectItem landing-a" target="_blank" href="#" rel="noopener noreferrer">
                <div className="landing-projectItemLeft">
                  <h1>实验室</h1>
                  <p>收集有趣html作品</p>
                </div>
                <div className="landing-projectItemRight">
                  <img src="/homepage/img/i4.png" alt="" />
                </div>
              </a>
              <a className="landing-projectItem landing-a" href="/toolbox" rel="noopener noreferrer">
                <div className="landing-projectItemLeft">
                  <h1>工具箱</h1>
                  <p>在线实用工具集合</p>
                </div>
                <div className="landing-projectItemRight">
                  <img src="/homepage/img/i5.png" alt="" />
                </div>
              </a>
            </div>

            <div className="landing-title">
              <svg viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
                <path d="M629.333333 202.666667v213.333333h277.333334v448h-512v-213.333333h-277.333334v-448h512z m213.333334 277.333333h-213.333334v170.666667h-170.666666v149.333333h384v-320z m-277.333334-213.333333h-384v320h213.333334v-170.666667h170.666666v-149.333333z m0 213.333333h-106.666666v106.666667h106.666666v-106.666667z" />
              </svg>
              project
            </div>
            <div className="landing-projectList">
              <a className="landing-projectItem landing-b" href="javascript:void(0)" onClick={() => navigate(configRef.current.landingRedirectUrl)}>
                <div className="landing-projectItemLeft">
                  <h1>数据管理</h1>
                  <p>管理自己零散的数据</p>
                </div>
                <div className="landing-projectItemRight">
                  <img src="/homepage/img/i3.png" alt="" />
                </div>
              </a>
            </div>

            <div className="landing-title">
              <svg viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
                <path d="M395.765333 586.570667h-171.733333c-22.421333 0-37.888-22.442667-29.909333-43.381334L364.768 95.274667A32 32 0 0 1 394.666667 74.666667h287.957333c22.72 0 38.208 23.018667 29.632 44.064l-99.36 243.882666h187.050667c27.509333 0 42.186667 32.426667 24.042666 53.098667l-458.602666 522.56c-22.293333 25.408-63.626667 3.392-54.976-29.28l85.354666-322.421333zM416.714667 138.666667L270.453333 522.581333h166.869334a32 32 0 0 1 30.933333 40.181334l-61.130667 230.954666 322.176-367.114666H565.312c-22.72 0-38.208-23.018667-29.632-44.064l99.36-243.882667H416.714667z" />
              </svg>
              skills
            </div>
            <div className="landing-skill">
              {/* <img id="skillPc" src="/homepage/svg/skillPc.svg" alt="" />
              <img id="skillWap" src="/homepage/svg/skillWap.svg" alt="" /> */}
            </div>
          </div>
        </div>
      </div>

      <footer className="landing-footer">
        Tomato © {new Date().getFullYear()}
        {icpNumber && (
          <> | <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer"> {icpNumber}</a></>
        )}
        {policeRecordNumber && (() => {
          const code = policeRecordNumber.match(/\d+/)?.[0] || '';
          const href = code
            ? `https://www.beian.gov.cn/portal/registerSystemInfo?recordcode=${code}`
            : 'https://www.beian.gov.cn/';
          return <> | <a href={href} target="_blank" rel="noopener noreferrer"> {policeRecordNumber}</a></>;
        })()}
      </footer>
    </div>
  );
};

export default LandingPage;
